
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Subscription, mapSubscriptionFromDb } from '@/services/types/subscriptionTypes';
import { useCourierInfo } from './useCourierInfo';
import { toast } from '@/components/ui/sonner';

export const useSubscriptions = () => {
  const { courierId, error: courierError } = useCourierInfo();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Функция загрузки подписок
  const fetchSubscriptions = useCallback(async () => {
    if (!courierId) {
      console.log('ID курьера не определен, загрузка подписок невозможна');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(`Загрузка подписок для курьера ${courierId}`);
      
      // Сначала получаем все аренды курьера
      const { data: rentals, error: rentalsError } = await supabase
        .from('rentals')
        .select('id, human_number')
        .eq('courier_id', courierId);
        
      if (rentalsError) {
        console.error('Ошибка при загрузке аренд курьера:', rentalsError);
        setError('Не удалось загрузить данные аренд');
        setLoading(false);
        return;
      }
      
      if (!rentals || rentals.length === 0) {
        console.log('У курьера нет аренд');
        setSubscriptions([]);
        setLoading(false);
        return;
      }
      
      // Получаем ID всех аренд курьера
      const rentalIds = rentals.map(rental => rental.id);
      
      // Теперь загружаем подписки для этих аренд
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('subscriptions')
        .select('*, rentals:rental_id(*)')
        .in('rental_id', rentalIds)
        .order('created_at', { ascending: false });
        
      if (subscriptionsError) {
        console.error('Ошибка при загрузке подписок:', subscriptionsError);
        setError('Не удалось загрузить данные подписок');
        setLoading(false);
        return;
      }

      console.log(`Загружено ${subscriptionsData?.length || 0} подписок для курьера ${courierId}`);
      
      // Преобразуем данные с правильной типизацией
      const formattedSubscriptions: Subscription[] = subscriptionsData?.map(sub => 
        mapSubscriptionFromDb(sub)
      ) || [];
      
      setSubscriptions(formattedSubscriptions);
    } catch (err: any) {
      console.error('Непредвиденная ошибка при загрузке подписок:', err);
      setError(`Произошла ошибка при загрузке данных: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [courierId]);

  // Загрузка подписок при изменении ID курьера
  useEffect(() => {
    if (courierId) {
      fetchSubscriptions();
    }
  }, [courierId, fetchSubscriptions]);

  // Обработчик обновления статуса подписки
  const handleStatusChange = async (subscriptionId: string, status: 'active' | 'paused' | 'cancelled') => {
    setActionLoading(subscriptionId);
    
    try {
      // Обновление статуса подписки
      const { error: updateError } = await supabase
        .from('subscriptions')
        .update({ status })
        .eq('id', subscriptionId);
        
      if (updateError) {
        throw new Error(updateError.message);
      }
      
      toast.success(`Статус подписки изменен на: ${status}`);
      
      // Обновляем список после изменения
      fetchSubscriptions();
    } catch (err: any) {
      console.error('Ошибка при изменении статуса подписки:', err);
      toast.error(`Не удалось изменить статус: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Функция для обновления списка подписок
  const handleRefresh = () => {
    fetchSubscriptions();
  };

  return {
    subscriptions,
    loading,
    error,
    actionLoading,
    handleStatusChange,
    handleRefresh
  };
};
